import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  TextField,
  Alert,
  Typography,
  Container,
  Paper,
  Box,
  CircularProgress,
  InputAdornment,
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";

import * as Yup from "yup";
import { useFormik } from "formik";
import useAuth from "../hooks/useAuth";

export default function LoginPage() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  const formik = useFormik({
    initialValues: { email: "", password: "" },
    validationSchema: Yup.object({
      email: Yup.string()
        .email("Neispravan email")
        .required("Email je obavezan"),
      password: Yup.string().required("Lozinka je obavezna"),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/login`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(values),
          },
        );

        const data = await response.json();

        if (response.ok && data.access_token) {
          login(data);
          navigate("/dashboard");
        } else {
          setError("Pogrešan email ili lozinka");
        }
      } catch (err) {
        setError("Problem s povezivanjem na server.");
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
        fontFamily: "'Inter', sans-serif",
      }}
    >
      <Container maxWidth="xs">
        <Paper
          elevation={0}
          sx={{
            p: 5,
            borderRadius: 4,
            boxShadow:
              "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
            bgcolor: "white",
            border: "1px solid #e2e8f0",
          }}
        >
          <Box mb={4} textAlign="center">
            <Typography
              variant="h4"
              sx={{
                fontWeight: 800,
                color: "#284c7a",
                mb: 1,
              }}
            >
              G3 Spirits
            </Typography>
          </Box>

          <Typography
            variant="h6"
            fontWeight="700"
            mb={3}
            textAlign="center"
            color="#1e293b"
          >
            Prijava
          </Typography>

          {error && (
            <Alert
              severity="error"
              sx={{ mb: 3, borderRadius: 2, fontWeight: 500 }}
              variant="filled"
            >
              {error}
            </Alert>
          )}

          <form onSubmit={formik.handleSubmit}>
            <TextField
              fullWidth
              label="Email adresa"
              name="email"
              margin="normal"
              autoComplete="email"
              placeholder="korisnicko.ime@g3spirits.ba"
              value={formik.values.email}
              onChange={formik.handleChange}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
              // NOVI NAČIN UMJESTO InputProps
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon sx={{ color: "#94a3b8", fontSize: 20 }} />
                    </InputAdornment>
                  ),
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  "& fieldset": { borderColor: "#e2e8f0" },
                },
              }}
            />
            <TextField
              fullWidth
              label="Lozinka"
              name="password"
              type="password"
              margin="normal"
              autoComplete="current-password"
              placeholder="lozinka"
              value={formik.values.password}
              onChange={formik.handleChange}
              error={formik.touched.password && Boolean(formik.errors.password)}
              helperText={formik.touched.password && formik.errors.password}
              // NOVI NAČIN UMJESTO InputProps
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon sx={{ color: "#94a3b8", fontSize: 20 }} />
                    </InputAdornment>
                  ),
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  "& fieldset": { borderColor: "#e2e8f0" },
                },
              }}
            />

            <Button
              fullWidth
              type="submit"
              variant="contained"
              disabled={loading}
              sx={{
                mt: 4,
                py: 1.8,
                borderRadius: 2,
                backgroundColor: "#284c7a",
                fontSize: "15px",
                fontWeight: "600",
                textTransform: "none",
                boxShadow: "0 4px 6px -1px rgba(40, 76, 122, 0.3)",
                transition: "all 0.2s",
                "&:hover": {
                  backgroundColor: "#1e3a5a",
                  boxShadow: "0 10px 15px -3px rgba(40, 76, 122, 0.4)",
                  transform: "translateY(-1px)",
                },
              }}
            >
              {loading ? (
                <CircularProgress size={24} sx={{ color: "white" }} />
              ) : (
                "Prijavi se u sustav"
              )}
            </Button>
          </form>

          <Box mt={4} textAlign="center">
            <Typography
              variant="caption"
              color="textSecondary"
              sx={{ fontWeight: 500 }}
            >
              © {new Date().getFullYear()} G3 Spirits d.o.o.
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}
